import { useState, useCallback, useEffect, useRef } from "react"
import { Button } from "~/react/ui/button"
import DiscoverFilters from "./discover_filters"
import ProactiveAiPrompt from "./proactive-ai-prompt"
import {
  DiscoverResults,
  DiscoverResultsTable,
  DiscoverResultsPagination,
  NoResults,
  UpgradePrompt,
  LoadingState,
} from "./discover_results"
import type {
  DiscoverFilters as DiscoverFiltersType,
  DiscoverPageProps,
  DiscoverSavedSearch,
  FilterSuggestion,
  SuggestionResponse,
} from "~/react/types/discover"
import { useDiscoverFilters } from "~/react/hooks/useDiscoverFilters"
import { useDiscoverResults } from "~/react/hooks/useDiscoverResults"
import {
  discover_ai_filters_path,
  discover_ai_filter_refinements_path,
  discover_views_path,
  discover_view_path,
  discover_suggestions_path,
} from "~/lib/routes"
import { getCsrfToken } from "~/lib/utils"
import {
  PageHeader,
  PageHeaderActions,
  PageHeaderTitle,
} from "../ui/page-header"
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet"
import MultiDomainSearch from "./multi_domain_search"
import SaveCompanies from "./discover/save-companies"
import { CompanyListsProvider } from "./company-lists-context"
import { LeadsListsProvider } from "./leads-lists-context"
import { CampaignsProvider } from "./campaigns-context"

const t = (key: string, options?: Record<string, unknown>) =>
  window.i18n.t(key, options)

export interface DiscoverProps {
  filterProps: DiscoverPageProps
}

export default function Discover({ filterProps }: DiscoverProps) {
  const [chatId, setChatId] = useState<number | null>(null)
  const [isAiLoading, setIsAiLoading] = useState(false)
  const [previousFilters, setPreviousFilters] = useState<DiscoverFiltersType>({})
  const [currentOffset, setCurrentOffset] = useState(
    filterProps.pagination?.offset || 0,
  )
  const [currentPageSize, setCurrentPageSize] = useState(
    filterProps.pagination?.pageSize || 20,
  )
  const [peopleFinderIsOpen, setPeopleFinderIsOpen] = useState(false)
  const [saveCompaniesIsOpen, setSaveCompaniesIsOpen] = useState(false)

  // Lift viewId and savedSearches to state for no-reload updates
  const [viewId, setViewId] = useState<number | null>(filterProps.viewId ?? null)
  const [savedSearches, setSavedSearches] = useState<DiscoverSavedSearch[]>(
    filterProps.savedSearches ?? [],
  )

  // Proactive suggestion state
  const [suggestion, setSuggestion] = useState<FilterSuggestion | null>(null)
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false)
  const suggestionFetchedForFilters = useRef<string | null>(null)
  const suggestionAbortController = useRef<AbortController | null>(null)

  const {
    results: initialResults,
    paths,
    hasFilters: serverHasFilters,
    entitlement,
  } = filterProps

  // Lift filter state to this component so AI prompt can update it
  const {
    filters,
    setFilters,
    updateFilter,
    clearAllFilters: baseClearAllFilters,
    hasFilters,
  } = useDiscoverFilters(filterProps.currentFilters)

  // Use the results hook for API-based fetching
  const {
    results,
    isLoading: isResultsLoading,
    hasFilters: resultsHasFilters,
    fetchResults,
    clearResults,
  } = useDiscoverResults({
    initialResults,
    initialHasFilters: serverHasFilters,
  })

  // Combined loading state
  // Only show full loading state when fetching results, not during AI refinement
  const isLoading = isResultsLoading

  // Wrap clearAllFilters to also reset chat context, viewId, and clear results
  const clearAllFilters = useCallback(() => {
    baseClearAllFilters()
    setChatId(null)
    setViewId(null) // Deselect saved search when clearing filters
    setCurrentOffset(0)
    clearResults()
  }, [baseClearAllFilters, clearResults])

  // Handle filter changes from DiscoverFilters component
  const handleFiltersChange = useCallback(
    (newFilters: DiscoverFiltersType) => {
      setCurrentOffset(0) // Reset to first page when filters change
      fetchResults(newFilters)
    },
    [fetchResults],
  )

  // Handle loading a saved search (no page reload)
  const handleLoadSavedSearch = useCallback(
    (search: DiscoverSavedSearch) => {
      setFilters(search.filters)
      setViewId(search.id)
      setChatId(null) // Reset AI chat context
      setCurrentOffset(0)
    },
    [setFilters],
  )

  // Handle saving/updating a saved search (no page reload)
  const handleSaveSearch = useCallback(
    async (name: string, existingViewId?: number): Promise<boolean> => {
      const isUpdate = !!existingViewId
      const url = isUpdate
        ? discover_view_path(existingViewId)
        : discover_views_path()
      const method = isUpdate ? "PUT" : "POST"

      // Clean filters (same logic as getFiltersJson in search_saver)
      const cleanFilters: Record<string, unknown> = {}
      Object.entries(filters).forEach(([key, value]) => {
        if (value === undefined || value === null) return
        if (Array.isArray(value) && value.length === 0) return
        if (typeof value === "object" && Object.keys(value).length === 0) return
        cleanFilters[key] = value
      })

      try {
        const response = await fetch(url, {
          method,
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCsrfToken(),
            Accept: "application/json",
          },
          body: JSON.stringify({
            view: { name, filters: JSON.stringify(cleanFilters) },
          }),
        })

        if (!response.ok) {
          ;(window as any).App.displayFlash(
            "error",
            t("js.back.discover.save_search.error"),
          )
          return false
        }

        const data = await response.json()

        if (isUpdate) {
          setSavedSearches((prev) =>
            prev.map((s) =>
              s.id === data.id
                ? { ...s, name: data.name, filters: data.filters }
                : s,
            ),
          )
        } else {
          setSavedSearches((prev) => [
            ...prev,
            { id: data.id, name: data.name, filters: data.filters },
          ])
          setViewId(data.id)
        }

        ;(window as any).App.displayFlash(
          "success",
          t("js.back.discover.save_search.success"),
        )
        return true
      } catch {
        ;(window as any).App.displayFlash(
          "error",
          t("js.back.discover.save_search.error"),
        )
        return false
      }
    },
    [filters],
  )

  // Update URL when viewId changes
  useEffect(() => {
    const url = new URL(window.location.href)
    if (viewId) {
      url.searchParams.set("view_id", String(viewId))
    } else {
      url.searchParams.delete("view_id")
    }
    window.history.replaceState(null, "", url.toString())
  }, [viewId])

  // Handle pagination
  const handlePageChange = useCallback(
    (newOffset: number) => {
      setCurrentOffset(newOffset)
      fetchResults(filters, newOffset)
    },
    [fetchResults, filters],
  )

  // Handle page size change
  const handlePageSizeChange = useCallback(
    (newPageSize: number) => {
      setCurrentPageSize(newPageSize)
      setCurrentOffset(0) // Reset to first page when page size changes
      fetchResults(filters, 0)
    },
    [fetchResults, filters],
  )

  const handleAiPromptSubmit = useCallback(
    async (query: string) => {
      setIsAiLoading(true)
      setPreviousFilters(filters) // Save current filters before update
      try {
        // Use refinements endpoint if we have existing filters
        const hasFilters = Object.keys(filters).length > 0
        const endpoint = hasFilters
          ? discover_ai_filter_refinements_path()
          : discover_ai_filters_path()

        const response = await fetch(endpoint, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-CSRF-Token": getCsrfToken(),
          },
          body: JSON.stringify({
            query,
            chat_id: chatId,
            ...(hasFilters && { current_filters: filters }),
          }),
        })

        if (!response.ok) {
          console.error("AI filter extraction failed:", response.status)
          return
        }

        const data = await response.json()

        // If AI returned a suggestion (query was unsuitable), show it
        if (data.suggestion) {
          setSuggestion(data.suggestion)
          return
        }

        setChatId(data.chat_id)

        // Update filters state with AI-extracted filters
        // This will trigger the auto-apply in DiscoverFilters
        const newFilters: DiscoverFiltersType = {
          ...data.filters,
          last_ai_query: query,
        }
        setFilters(newFilters)
      } catch (error) {
        console.error("AI filter extraction failed:", error)
      } finally {
        setIsAiLoading(false)
      }
    },
    [chatId, filters, setFilters],
  )

  // Fetch proactive suggestions after results are loaded
  const fetchSuggestion = useCallback(async () => {
    const filtersKey = JSON.stringify(filters)
    // Don't fetch if we already fetched for these exact filters
    if (suggestionFetchedForFilters.current === filtersKey) return
    suggestionFetchedForFilters.current = filtersKey

    // Abort any in-flight request
    suggestionAbortController.current?.abort()
    const controller = new AbortController()
    suggestionAbortController.current = controller

    setIsSuggestionLoading(true)
    try {
      const response = await fetch(discover_suggestions_path(), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": getCsrfToken(),
        },
        body: JSON.stringify({
          current_filters: filters,
          query: filters.last_ai_query || "",
          current_count: results?.numberOfDomains ?? 0,
        }),
        signal: controller.signal,
      })

      if (response.ok) {
        const data: SuggestionResponse = await response.json()
        setSuggestion(data.suggestion)
      }
    } catch (error) {
      if (error instanceof Error && error.name === "AbortError") return
      console.error("Failed to fetch suggestions:", error)
    } finally {
      setIsSuggestionLoading(false)
    }
  }, [filters, results])

  // Reset suggestion when filters change
  useEffect(() => {
    setSuggestion(null)
    suggestionFetchedForFilters.current = null
    suggestionAbortController.current?.abort()
  }, [filters])

  // Trigger suggestion fetch after results are loaded
  useEffect(() => {
    // Only fetch suggestions when:
    // 1. We have filters applied
    // 2. We're not currently loading results or AI
    // 3. Results have been fetched (even if 0 - that's when we need suggestions most!)
    if (!resultsHasFilters || isResultsLoading || isAiLoading || results === null) return

    // Debounce the fetch
    const timer = setTimeout(fetchSuggestion, 1500)
    return () => clearTimeout(timer)
  }, [resultsHasFilters, isResultsLoading, isAiLoading, results, fetchSuggestion])

  // Handler for applying suggestion
  const handleApplySuggestion = useCallback(() => {
    if (!suggestion) return
    setFilters(suggestion.filters)
    setSuggestion(null)
    suggestionFetchedForFilters.current = null // Reset to allow new fetch
  }, [suggestion, setFilters])

  // Handler for ignoring suggestion
  const handleIgnoreSuggestion = useCallback(() => {
    setSuggestion(null)
  }, [])

  // Determine what to render in the content area
  const renderContent = () => {
    // No filters applied - show AI prompt (let it handle its own loading state)
    if (!resultsHasFilters) {
      return (
        <div className="board-box">
          <div className="discover-content">
            <span className="discover-content__title">
              {t("js.discover.title")}
            </span>
            <ProactiveAiPrompt onSubmit={handleAiPromptSubmit} />
            <span className="discover-content__caption">
              {t("js.discover.caption")}{" "}
              <Button variant="linkGrey" size="sm" asChild>
                <a href="/pricing">{t("js.discover.caption_link")}</a>
              </Button>
            </span>
          </div>
        </div>
      )
    }

    // Loading results after filters are applied
    if (isLoading) {
      return <LoadingState />
    }

    // Filters applied but no results
    if (!results || results.domains.length === 0) {
      return <NoResults />
    }

    // We have results - render them
    return (
      <LeadsListsProvider initialLeadsLists={filterProps.leadsLists}>
        <CampaignsProvider initialCampaigns={filterProps.campaigns}>
          <CompanyListsProvider initialCompanyLists={filterProps.companyLists}>
            <div className="board-box discover-results-container">
              <PageHeader className="mb-0">
                <PageHeaderTitle>
                  {t("js.discover.index.results_match_your_filters", {
                    count: results.numberOfDomains,
                    results: results.numberOfDomains.toLocaleString(),
                  })}
                </PageHeaderTitle>
                <PageHeaderActions>
                  <Sheet
                    open={saveCompaniesIsOpen}
                    onOpenChange={setSaveCompaniesIsOpen}
                    modal={false}
                  >
                    <SheetTrigger asChild>
                      <Button size="sm">
                        {t("js.discover.index.save_all_companies")}
                      </Button>
                    </SheetTrigger>
                    <SheetContent className="p-0" overlay={false}>
                      <SaveCompanies
                        numberOfDomains={results.numberOfDomains}
                        domains={results.domains}
                        filters={filters}
                        closeSidepanel={() => setSaveCompaniesIsOpen(false)}
                      />
                    </SheetContent>
                  </Sheet>
                  <Sheet
                    open={peopleFinderIsOpen}
                    onOpenChange={setPeopleFinderIsOpen}
                  >
                    <SheetTrigger asChild>
                      <Button variant="primary" size="sm">
                        {t("js.discover.index.find_all_people", {
                          count: results.numberOfEmailAddresses,
                          results:
                            results.numberOfEmailAddresses.toLocaleString(),
                        })}
                      </Button>
                    </SheetTrigger>
                    <SheetContent size="lg" className="p-0">
                      <MultiDomainSearch
                        numberOfCompanies={results.numberOfDomains}
                        numberOfResults={results.numberOfEmailAddresses}
                        genericEmailsCount={0}
                        personalEmailsCount={0}
                        allowDiscoverPaging={entitlement.allowDiscoverPaging}
                        filterParams={filters}
                        numberOfCreditsLeft={filterProps.numberOfCreditsLeft}
                        leadsLists={filterProps.leadsLists}
                        campaigns={filterProps.campaigns}
                        type="domains"
                        source="discover"
                        clickToCopy={false} // TODO: Use real entitlement value
                      />
                    </SheetContent>
                  </Sheet>
                </PageHeaderActions>
              </PageHeader>
              <DiscoverResults>
                <DiscoverResultsTable
                  domains={results.domains}
                  domainsWithRecentFundings={results.domainsWithRecentFundings}
                  domainsCurrentlyHiring={results.domainsCurrentlyHiring}
                  numberOfCreditsLeft={filterProps.numberOfCreditsLeft}
                  singleCreditsBucket={entitlement.singleCreditsBucket}
                />
                {entitlement.allowDiscoverPaging ? (
                  <DiscoverResultsPagination
                    pageSize={currentPageSize}
                    offset={currentOffset}
                    hasMoreDomains={results.hasMoreDomains}
                    hasDomains={results.domains.length > 0}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                  />
                ) : (
                  results.hasMoreDomains &&
                  paths && (
                    <UpgradePrompt
                      remainingCount={
                        results.numberOfDomains - results.domains.length
                      }
                      paths={paths}
                    />
                  )
                )}
              </DiscoverResults>
            </div>
          </CompanyListsProvider>
        </CampaignsProvider>
      </LeadsListsProvider>
    )
  }

  return (
    <main className="dashboard-main">
      <div className="dashboard-main__filters discover-filters">
        <DiscoverFilters
          {...filterProps}
          viewId={viewId}
          savedSearches={savedSearches}
          filters={filters}
          updateFilter={updateFilter}
          clearAllFilters={clearAllFilters}
          hasFilters={hasFilters}
          showInlinePrompt={hasFilters || !!suggestion}
          onAiPromptSubmit={handleAiPromptSubmit}
          onFiltersChange={handleFiltersChange}
          onLoadSavedSearch={handleLoadSavedSearch}
          onSaveSearch={handleSaveSearch}
          isAiLoading={isAiLoading}
          previousFilters={previousFilters}
          suggestion={suggestion}
          isSuggestionLoading={isSuggestionLoading}
          onApplySuggestion={handleApplySuggestion}
          onIgnoreSuggestion={handleIgnoreSuggestion}
        />
      </div>
      <div className="dashboard-main__content">{renderContent()}</div>
    </main>
  )
}
